import { useCallback, useEffect, useRef } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { useDispatch } from 'react-redux'

import {
  type MenuRecipeTileRequestParams,
  menuRecipeTileFetcher,
  type RecipeUUID,
  transformMenuRecipeTileResponseToRecipe,
} from '@library/menu-service'

import { menuRecipeTileReceived } from 'actions/menu/menuV3'

export type RefreshRecipeTile = (
  recipeId: RecipeUUID,
  numPortions: MenuRecipeTileRequestParams['numPortions'],
) => Promise<void>

type UseRecipeTileRefreshArgs = {
  deliveryDate?: string
  menuId?: string | null
}

type LatestRecipeTileRequest = {
  deliveryDate?: string
  menuId: string
  numPortions: MenuRecipeTileRequestParams['numPortions']
  ownerId: symbol
}

type LatestRecipeTileRequestByRecipeId = Partial<Record<RecipeUUID, LatestRecipeTileRequest>>

const latestRecipeTileRequestByRecipeId: LatestRecipeTileRequestByRecipeId = {}

export const useRecipeTileRefresh = ({ deliveryDate, menuId }: UseRecipeTileRefreshArgs) => {
  const dispatch = useDispatch()
  const ownerId = useRef(Symbol('recipe-tile-refresh-owner'))

  useEffect(
    () => () => {
      Object.entries(latestRecipeTileRequestByRecipeId).forEach(([recipeId, request]) => {
        if (request?.ownerId === ownerId.current) {
          delete latestRecipeTileRequestByRecipeId[recipeId as RecipeUUID]
        }
      })
    },
    [deliveryDate, menuId],
  )

  const refreshRecipeTile = useCallback<RefreshRecipeTile>(
    async (recipeId, numPortions) => {
      if (!menuId) {
        return
      }

      const latestRecipeTileRequest = {
        deliveryDate,
        menuId,
        numPortions,
        ownerId: ownerId.current,
      }

      latestRecipeTileRequestByRecipeId[recipeId] = latestRecipeTileRequest

      try {
        const recipeTile = await menuRecipeTileFetcher({
          menuId,
          recipeId,
          numPortions,
          ...(deliveryDate && { deliveryDate }),
        })

        if (latestRecipeTileRequestByRecipeId[recipeId] !== latestRecipeTileRequest) {
          return
        }

        const refreshedRecipe = transformMenuRecipeTileResponseToRecipe(recipeTile)

        dispatch(menuRecipeTileReceived(refreshedRecipe))
      } catch {
        datadogRum.addAction('recipe_tile_refresh_failed', {
          menu_id: menuId,
          recipe_id: recipeId,
          num_portions: numPortions,
        })
      }
    },
    [deliveryDate, dispatch, menuId],
  )

  return {
    refreshRecipeTile,
  }
}
